import React, { useState, useEffect } from 'react';
import { X, Clock, Plus } from 'lucide-react';
import { FlatRateItem, FlatRateCategory, createFlatRateItem, updateFlatRateItem, getFlatRateCategories, createFlatRateCategory } from '../lib/flat-rates';
import { selectInputContentOnFocus } from '../lib/utils';

interface FlatRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
  item?: FlatRateItem | null;
  mode: 'create' | 'edit';
}

export default function FlatRateModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  businessId,
  item,
  mode 
}: FlatRateModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<FlatRateCategory[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    hours: '',
    category_id: ''
  });

  const handleAddCategory = async () => {
    if (!businessId || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createFlatRateCategory({
        business_id: businessId,
        name: newCategoryName.trim(),
        order_index: categories.length
      });
      
      await loadCategories();
      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    if (!businessId) return;
    
    try {
      const data = await getFlatRateCategories(businessId);
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCategories();
      if (mode === 'edit' && item) {
        setFormData({
          name: item.name,
          hours: item.hours.toString(),
          category_id: item.category_id || ''
        });
      }
    }
  }, [isOpen, item, mode]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        name: '',
        hours: '',
        category_id: ''
      });
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (mode === 'create') {
        await createFlatRateItem({
          business_id: businessId,
          name: formData.name,
          category_id: formData.category_id || null,
          hours: parseFloat(formData.hours) || 0,
          order_index: 0
        });
      } else if (mode === 'edit' && item) {
        await updateFlatRateItem(item.id, {
          name: formData.name,
          category_id: formData.category_id || null,
          hours: parseFloat(formData.hours) || 0
        });
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving flat rate item:', err);
      setError(err instanceof Error ? err.message : 'Failed to save flat rate item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              {mode === 'create' ? 'New Flat Rate Item' : 'Edit Flat Rate Item'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  placeholder="Enter item name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                {isAddingCategory ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter category name"
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingCategory(false);
                          setNewCategoryName('');
                        }}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={!newCategoryName.trim() || loading}
                        className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      >
                        {loading ? 'Creating...' : 'Create Category'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex space-x-2">
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                      className="flex-1 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    >
                      <option value="">No Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setIsAddingCategory(true)}
                      className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 flex items-center"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      New
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hours
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.1"
                    value={formData.hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                    onFocus={selectInputContentOnFocus}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    placeholder="Enter hours"
                  />
                  <Clock className="h-5 w-5 ml-2 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create Item' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}